import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';

import { FlowAnalyticsExtensions } from '../resources/FlowAnalyticsExtensions';
import { grey230border } from '../styles/FlowStyleConstants';
import { AnalyticsExtensionConnectionPicker } from './AnalyticsExtensionConnectionPicker';
import { useFlowAnalyticsExtensionEditor } from './useFlowAnalyticsExtensionEditor';

const sectionClass = style({ marginTop: TabStyles.Spacing.LargeSpace, paddingTop: TabStyles.Spacing.LargeSpace,
  paddingBottom: TabStyles.Spacing.LargeSpace, borderTop: grey230border, borderBottom: grey230border });
const warningClass = style({ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'bold' });
const descriptionClass = style({ marginTop: TabStyles.Spacing.MediumSpace, marginBottom: TabStyles.Spacing.MediumSpace });
const rowClass = style({ display: 'flex', alignItems: 'flex-start', gap: TabStyles.Spacing.MediumSpace });
const pickerClass = style({ flex: '1 1 auto', minWidth: 0 });
const buttonClass = style({ flex: '0 0 auto', marginTop: 26 });
const errorClass = style({ marginTop: TabStyles.Spacing.DefaultSpace });

export interface PublishedFlowAnalyticsExtensionSectionProps {
  readonly flowLuid: string;
  readonly onApplied?: () => void;
}

export const PublishedFlowAnalyticsExtensionSection: React.FC<PublishedFlowAnalyticsExtensionSectionProps> = ({ flowLuid, onApplied }) => {
  const load = React.useCallback(async () => {
    const [options, connection] = await Promise.all([
      FlowAnalyticsExtensions.getOptions(flowLuid),
      FlowAnalyticsExtensions.getSelection(flowLuid),
    ]);
    return { options, connection };
  }, [flowLuid]);
  const editor = useFlowAnalyticsExtensionEditor({
    load,
    applyConnection: connectionLuid => FlowAnalyticsExtensions.updateConnection(flowLuid, connectionLuid),
    onApplied,
  });

  return (
    <div className={sectionClass} data-tb-test-id="published-flow-analytics-extension-section">
      {editor.loaded && !editor.connection && <>
        <div className={warningClass}>
          <div className="tb-dialogs-warning" />
          <span>{Messages.publishFlowSuccessDialogAnExt_noConnectorSelected()}</span>
        </div>
        <div className={descriptionClass}>{Messages.publishFlowSuccessDialogAnExt_noConnectionWarning()}</div>
      </>}
      <div className={rowClass}>
        <div className={pickerClass}>
          <AnalyticsExtensionConnectionPicker {...editor.pickerProps} />
        </div>
        <ButtonWidget className={buttonClass} testId="published-flow-analytics-extension-apply" buttonType={ButtonType.Outline}
          disabled={editor.saving || !editor.canApply} onClick={editor.apply}>
          {Messages.apply()}
        </ButtonWidget>
      </div>
      {editor.saveFailed && <div className={errorClass} role="alert">{Messages.flowAnalyticsExtensionSaveError()}</div>}
    </div>
  );
};
